import { useEffect, useState } from 'react'
import { COMMENT_PARENT_KINDS, type CommentParentKind } from './comments'

/**
 * Minimal URL state, deliberately not a router.
 *
 * The app is a handful of tabs and a detail drawer. What it needs from the address bar is that a
 * link like `?decision=D2-081` opens that decision, that the back button closes what it
 * opened, and that the address is copyable. A routing library would restructure the whole
 * component tree to deliver exactly that. If this grows nested routes or code splitting
 * per tab, TanStack Router is the upgrade; today it would be scaffolding around a
 * two-parameter problem.
 */

/** Taken from the collection mapping rather than listed again, so the two cannot diverge. */
const KNOWN_KINDS = new Set<string>(Object.values(COMMENT_PARENT_KINDS))

export type UrlState = {
  tab: string | null
  decision: string | null
  field: string | null
  picklist: string | null
  product: string | null
  task: string | null
  owner: string | null
  update: string | null
  view: string | null
  person: string | null
  container: string | null
  note: string | null
  /** A row of the sharing rules table, the org chart's third sub-tab. */
  sharingRule: string | null
  /**
   * Which comment inside the open item to land on. Not an item id — it never decides a
   * tab — but it is cleared alongside them, because a comment belongs to the item it was
   * written on and carrying it across to another one would point at nothing.
   */
  comment: string | null
  message: string | null
}

const read = (): UrlState => {
  const p = new URLSearchParams(window.location.search)
  return {
    tab: p.get('tab'),
    decision: p.get('decision'),
    field: p.get('field'),
    picklist: p.get('picklist'),
    product: p.get('product'),
    task: p.get('task'),
    owner: p.get('owner'),
    update: p.get('update'),
    view: p.get('view'),
    person: p.get('person'),
    container: p.get('container'),
    note: p.get('note'),
    sharingRule: p.get('sharingRule'),
    comment: p.get('comment'),
    message: p.get('message'),
  }
}

/**
 * Everything reading the address, so a change made in one place reaches the others.
 *
 * `useUrlState` keeps its own copy in React state, and `useFocusedComment` — read by the
 * discussion inside whichever drawer is open — keeps another. Without this, clicking a
 * notification while a drawer was already open pushed the new address but only told the
 * copy that pushed it, so the discussion never learned which comment to go to.
 */
const listeners = new Set<(next: UrlState) => void>()

function subscribe(fn: (next: UrlState) => void) {
  const onPop = () => fn(read())
  listeners.add(fn)
  window.addEventListener('popstate', onPop)
  return () => {
    listeners.delete(fn)
    window.removeEventListener('popstate', onPop)
  }
}

/**
 * Moves the address, and tells everything reading it.
 *
 * A module function rather than a method on the hook, because two things outside React's
 * tree have to be able to navigate: a `#`-reference clicked inside a rendered comment,
 * and anything else that ends up holding an address without holding the hook. The hook's
 * `setUrl` is this function.
 */
export function applyUrl(next: Partial<UrlState>, mode: 'push' | 'replace' = 'push') {
  const merged = { ...read(), ...next }
  const p = new URLSearchParams()
  if (merged.tab && merged.tab !== 'home') p.set('tab', merged.tab)
  for (const key of Object.keys(CLEARED_ITEM_IDS) as ItemIdKey[]) {
    const value = merged[key]
    if (value) p.set(key, value)
  }
  if (merged.view) p.set('view', merged.view)

  const qs = p.toString()
  const url = `${window.location.pathname}${qs ? `?${qs}` : ''}`
  window.history[mode === 'push' ? 'pushState' : 'replaceState'](null, '', url)
  for (const fn of listeners) fn(merged)
}

export function useUrlState() {
  const [state, setState] = useState<UrlState>(read)

  // Back and forward have to move through the drawer, not jump past it.
  useEffect(() => subscribe(setState), [])

  // `applyUrl` is already stable — it is a module function — so there is nothing here to
  // memoise. It is returned as it is rather than wrapped, which is also what makes the
  // hook's `setUrl` and a reference link's navigation literally the same code.
  return { url: state, setUrl: applyUrl }
}

/**
 * The comment the address is pointing at, for the discussion that has to scroll to it.
 *
 * A hook rather than a prop because `<Discussion>` is mounted from seven different
 * drawers, none of which has any other reason to know about the address bar.
 */
export function useFocusedComment(): string | null {
  const [id, setId] = useState<string | null>(() => read().comment)
  useEffect(() => subscribe((next) => setId(next.comment)), [])
  return id
}

/**
 * Every item id in `UrlState`, cleared.
 *
 * This list existed as four hand-written copies — here, in `App.tsx`'s `setTab`, in
 * `ActionPanels.tsx`'s `TAB()`, and in `events.ts`'s `urlForActivity` gate branch — because
 * each site worked out "every other id" for itself instead of sharing one answer. Three of
 * the four were missed when `person` and `container` were added, and `picklist` was missing
 * from some of them even before that — the same mistake, made twice, by the same cause. A
 * stale entry is not cosmetic: `tabForUrl` reads any leftover id as its own tab, so a link
 * carrying an id nobody cleared strands the reader on a tab no tab-bar click can escape.
 *
 * Typed as `Record<ItemIdKey, null>` rather than a plain object literal so that adding a
 * field to `UrlState` without adding it here is a compile error, not a fifth place to
 * forget.
 *
 * `view` is deliberately not an item id. It is a sub-tab preference — chart vs. roster on
 * Org Chart — not which item is open, so crossing to a different item must not reset it.
 */
export type ItemIdKey = Exclude<keyof UrlState, 'tab' | 'view'>

export const CLEARED_ITEM_IDS: Record<ItemIdKey, null> = {
  decision: null,
  field: null,
  picklist: null,
  product: null,
  task: null,
  owner: null,
  update: null,
  person: null,
  container: null,
  note: null,
  sharingRule: null,
  comment: null,
  message: null,
}

/** The tab a deep link implies, so `?decision=…` lands on Decisions without saying so. */
export const tabForUrl = (s: UrlState): string =>
  s.decision ? 'decisions'
  : s.field ? 'fields'
  : s.picklist ? 'picklists'
  : s.product ? 'products'
  : s.task ? 'roadmap'
  : s.owner ? 'owners'
  : s.update ? 'status'
  : s.person ? 'orgchart'
  : s.container ? 'orgchart'
  : s.message ? 'messages'
  : s.note ? 'orgchart'
  : s.sharingRule ? 'orgchart'
  : (s.tab ?? 'home')

/**
 * The address of one item, as URL state.
 *
 * Every other id is cleared on the way. `tabForUrl` reads any leftover one as its own
 * tab, so a stale `task` would put the previous drawer back the moment somebody pressed
 * back — and a link that carried two ids would open the wrong one.
 *
 * This lives here rather than in whichever feature needed it first, because the search
 * and the @mention notifications both have to turn "a kind and an id" into an address,
 * and the two answers have to be the same one. They were not: the mention link pasted the
 * kind straight into the query string and the products drawer described itself as a
 * field, so a mention on a product linked to the field guide.
 */
export function urlForKind(kind: CommentParentKind, targetId: string): Partial<UrlState> {
  // `view` is deliberately left out of the spread: it names which sub-tab (chart or
  // roster) the reader was on, not which item is open, so crossing to a different item
  // should not reset it. See `CLEARED_ITEM_IDS` for the full reasoning.
  const blank = { ...CLEARED_ITEM_IDS }
  switch (kind) {
    case 'decision':     return { ...blank, tab: 'decisions', decision: targetId }
    case 'task':         return { ...blank, tab: 'roadmap', task: targetId }
    case 'field':        return { ...blank, tab: 'fields', field: targetId }
    case 'product':      return { ...blank, tab: 'products', product: targetId }
    case 'owner':        return { ...blank, tab: 'owners', owner: targetId }
    case 'staff':        return { ...blank, tab: 'orgchart', person: targetId }
    case 'orgContainer': return { ...blank, tab: 'orgchart', container: targetId }
    case 'orgNote':      return { ...blank, tab: 'orgchart', note: targetId }
    // A sharing rule is a row on the chart's third sub-tab, addressed by its own document
    // id — `?sharingRule=work-item`. `view` is left alone by `blank`, so a reader already
    // on the Sharing rules sub-tab stays there.
    case 'orgSharingRule': return { ...blank, tab: 'orgchart', sharingRule: targetId }
    case 'message':      return { ...blank, tab: 'messages', message: targetId }
  }
}

/**
 * The id the address uses for one item.
 *
 * A decision is addressed by its reference — `?decision=D2-081` — and everything else by
 * its document id. Both the mention email and the notification bell have to turn the same
 * stored comment into the same address, so the rule lives here rather than as a ternary
 * inside whichever of them was written first.
 */
export function targetIdForKind(kind: CommentParentKind, docId: string, ref: string): string {
  return kind === 'decision' ? ref : docId
}

/**
 * The address a stored mention opens, or null if it cannot be trusted to open anything.
 *
 * `itemType` and `itemId` were recorded by whichever screen the comment was written on,
 * and until the bell started routing nothing ever read them back — so nothing ever caught
 * one being wrong. Notifications written before September 2026 can say `field` for a
 * product. That particular case is indistinguishable after the fact and will open the
 * field guide; what this can refuse is the rest — a kind that is not a known one, or
 * a missing id — because opening the wrong record is worse than opening nothing. The
 * reader can see that nothing happened; they cannot see that they were shown the wrong
 * thing.
 *
 * `commentId` is what makes the row land on the sentence somebody actually wrote. An item
 * under discussion can carry dozens of comments, and opening the drawer at the top of the
 * list leaves the reader to find the one they were told about. Notifications written
 * before September 2026 do not carry it; those still open the item, as they always did.
 */
export function urlForNotification(
  n: { itemType: CommentParentKind; itemId: string; itemRef: string; commentId?: string | null },
): Partial<UrlState> | null {
  if (!KNOWN_KINDS.has(n.itemType)) return null
  const targetId = targetIdForKind(n.itemType, n.itemId, n.itemRef)
  if (!targetId) return null
  return { ...urlForKind(n.itemType, targetId), comment: n.commentId ?? null }
}

/**
 * The same address, absolute, for a notification or an email — where a relative link is
 * no use at all. The tab is left out: `tabForUrl` works it out from the id, and a link
 * that says both would be two things to keep in step.
 */
export function hrefForItem(
  kind: CommentParentKind, targetId: string, commentId?: string | null,
): string {
  const state = { ...urlForKind(kind, targetId), comment: commentId ?? null }
  const params = new URLSearchParams()
  // Every item id, taken from `CLEARED_ITEM_IDS` rather than listed again here.
  //
  // This was a hand-written list, and it rotted exactly the way a hand-written list does:
  // `message` was added as a comment kind and never added here, so a notification or a
  // mention email about a message thread built an address carrying NO id at all — which
  // `tabForUrl` then reads as 'home'. The reader landed on the Overview page with nothing
  // open, which is precisely the failure this function exists to prevent. `picklist` and
  // `update` were missing too.
  //
  // `CLEARED_ITEM_IDS` is `Exclude<keyof UrlState, 'tab' | 'view'>`, so the next kind
  // someone adds is carried here by the compiler instead of by whoever remembers to.
  for (const key of Object.keys(CLEARED_ITEM_IDS) as Array<keyof typeof CLEARED_ITEM_IDS>) {
    const value = state[key]
    if (value) params.set(key, value)
  }
  return `${window.location.origin}/?${params.toString()}`
}
